import Vue from 'vue'
import { localize } from 'vee-validate'
import countries from '~/static/data/countries.json'
import { objectKeyValuesSwap, setBodyClass, Cookies } from '~/assets/util/helpers'
import { loadLocale } from '~/plugins/vee-validate'

export const state = () => {
	return {
		data: null,
		utm: null,
		geo: null,
		location: null,
		paymentUser: null,
		paymentUserFetched: false,
		sportTypes: []
	}
}

const initialState = Object.assign({}, state())

export const getters = {
	user ({ data, paymentUser }, getters, rootState) {
		const atlitData = {
			id: data?.id,
			type: data?.type
		}
		const paymentData = {
			customer: paymentUser,
			clubName: paymentUser?.clubName,
			phone: paymentUser?.phone,
			address: paymentUser?.address
		}

		return Object.assign({}, atlitData, rootState.auth.user, paymentData)
	},
	permissions ({ data, paymentUser }, getters, rootState, rootGetters) {
		const isAuthenticated = rootGetters['auth/isAuthenticated']
		const isEmailVerified = rootGetters['auth/isEmailVerified']
		const hasUserEmail = rootGetters['auth/hasUserEmail']

		const hasAtlitUser = Boolean(data)
		const hasSubscription = paymentUser && paymentUser.hasSubscription
		const hasTeam = rootGetters['teams/selectedTeam']

		const isIncompletePaidUser = !isEmailVerified || !hasSubscription || !hasTeam
		const isVerifiedAdmin = isEmailVerified && !isIncompletePaidUser
		const hasPaymentUser = paymentUser && Object.keys(paymentUser).length > 0

		const isTeamAccount = isAuthenticated && getters.accountType === 'team'
		const isClubAccount = isAuthenticated && getters.accountType === 'club'

		return {
			hasAtlitUser,
			isAuthenticated,
			isEmailVerified,
			isVerifiedAdmin,
			isIncompletePaidUser,
			isTeamAccount,
			isClubAccount,
			hasPaymentUser,
			hasSubscription,
			hasTeam: hasTeam ? Object.keys(hasTeam).length > 0 : Boolean(hasTeam),
			hasUserEmail
		}
	},
	region (state, getters) {
		return {
			origin: getters.origin,
			location: state.location,
			isUS: getters.origin === 'us',
			isUK: getters.origin === 'gb',
			isEU: state.location?.region === 'eu',
			isROW: state.location?.region === 'row',
			isAU: state.location?.region === 'au',
			isCA: getters.origin === 'ca'
		}
	},
	origin ({ location, paymentUser }) {
		const country = paymentUser && paymentUser.address && paymentUser.address.country
		const countriesMap = objectKeyValuesSwap(countries)

		return countriesMap[country] || location?.code || 'xx'
	},
	consultationLink () {
		return 'https://scheduler.zoom.us/d/hr5uw8oh/pixellot-air-troubleshooting-onboarding'
	},
	scheduleVSTrainingLink () {
		return 'https://scheduler.zoom.us/successteam'
	},
	accountType ({ paymentUser }) {
		return (paymentUser && paymentUser.accountType) || 'team'
	},
	sportTypes (state, getters) {
		return state.sportTypes
	}
}

export const actions = {
	async fetchUser ({ commit, dispatch }) {
		try {
			const { data } = await this.$axios.$get('/api/v1/users/me')

			this.$log.debug('[UserStore] Got `user` data', data)

			commit('setUser', data)
			dispatch('analytics/identify', { atlitUserId: data.id, atlitUserType: data.type }, { root: true })
			commit('events/setUserFeedbackRate', data.attributes.custom?.feedback, { root: true })
			commit('events/setTagsBubble', data.attributes.custom?.tagsBubble, { root: true })
			commit('events/setTFSurveyShown', data.attributes.custom?.TFSurveyShown || data.attributes.custom?.setTFSurveyShown, { root: true })
			commit('analytics-popup/setAnalyticsPopupShown', data.attributes.custom?.analyticsPopupShown, { root: true })
		} catch (error) {
			this.$log.error('Failed to fetch user', error)
		}
	},
	async fetchPaymentUser ({ commit, dispatch, getters, rootGetters }) {
		try {
			// For cases if code bellow will fail we still saving selection to have at least user/team selected on wizard
			commit('products/setWizardSelection',
				{ user: getters.user, team: rootGetters['teams/selectedTeam'] },
				{ root: true }
			)

			const { data } = await this.$axios.get('/api/v1/payment/users/profile')

			this.$log.debug('[UserStore] Got `payment user` data', data)

			commit('setPaymentUser', data.customer)
			commit('teams/selectTeam', data.activeTeam, { root: true })
			commit('products/setWizardSelection',
				{ selection: data.customer.selection, user: getters.user, team: rootGetters['teams/selectedTeam'] },
				{ root: true }
			)

			const teamId = data.activeTeam && data.activeTeam.id

			dispatch('analytics/identify', { teamId }, { root: true })
		} catch (error) {
			this.$log.error('Failed to fetch payment user', error)
		}
	},

	async createPaymentUser ({ commit }, payload) {
		const { data } = await this.$axios.post('/api/v1/payment/users/customer', payload, { tokenForceUpdate: true })

		this.$log.info('[UserStore] Created `payment user` data', { payload, response: data })

		commit('setPaymentUser', data)
	},

	async updatePaymentUser ({ commit, dispatch, getters, rootGetters }, payload) {
		const { data } = await this.$axios.put('/api/v1/payment/users/customer', payload)

		commit('setPaymentUser', data)
		commit('products/setWizardSelection',
			{ selection: payload.selection, user: getters.user, team: rootGetters['teams/selectedTeam'] },
			{ root: true }
		)

		dispatch('analytics/identify', {}, { root: true })
		this.$log.info('[UserStore] Updated `payment user` data', { payload, response: data })
	},
	async fetchLocation ({ commit }) {
		try {
			const response = await this.$axios.get('/api/v1/payment/users/signup/hints', { skipAuth: true })

			if (!response.data) {
				return commit('setLocation', null)
			}

			commit('setLocation', { code: response.data.country.toLowerCase(), region: response.data.region })
		} catch (error) {
			commit('setLocation', null)
			this.$log.error('Failed to fetch location', error)
		}
	},

	setLanguage ({ commit }, lang) {
		this.$i18n.setLocale(lang)
		localize(lang)
		loadLocale(this.$i18n, lang)
		setBodyClass(lang)
		Cookies.set('user_lang', lang)
	},

	checkCurrentLanguage () {
		const bodyClassName = document.body.className
		const locale = this.$i18n.locale

		bodyClassName !== locale && setBodyClass(locale)
		localize(locale)
	},

	async fetchSportTypes ({ commit, getters }) {
		const { data } = await this.$axios.get('/api/v1/payment/sport-types', { skipAuth: true })
			.catch(() => commit('setSportTypes', []))

		if (Array.isArray(data) && data.length > 0) {
			const mappedSportTypes = data.map((type) => {
				type.title = this.$i18n.tc(`common.sportTypes.${type.name}`, getters.region.isUS ? 0 : 1)
				return type
			})

			commit('setSportTypes', mappedSportTypes)
		}
	},

	getGeolocation ({ commit }) {
		return new Promise((resolve, reject) => {
			if (window.navigator.geolocation) {
				window.navigator.geolocation.watchPosition((geo) => {
					commit('setGeolocation', geo.coords)
					resolve(geo.coords)
				})
			} else {
				reject(new Error('Geolocation API is not supported by this browser.'))
			}
		})
	}
}

export const mutations = {
	RESET_STORE: (state) => {
		Object.assign(state, initialState)
	},

	setUTM (state, payload) {
		state.utm = payload
	},

	setUser (state, payload) {
		state.data = payload
	},

	setPaymentUser (state, payload) {
		state.paymentUser = payload
		state.paymentUserFetched = true
	},

	setGeolocation (state, geo) {
		Vue.set(state, 'geo', geo)
	},

	setLocation (state, payload) {
		if (payload) {
			const region = payload.region
			let countryCode = payload.code
			countryCode = countryCode.toLowerCase() === 'uk' ? 'gb' : countryCode

			state.location = {
				code: countryCode,
				region
			}
		} else {
			state.location = null
		}
	},

	setSportTypes (state, payload) {
		state.sportTypes = payload
	}
}
